---
title: "Crypto Basics: Bitcoin, Ethereum, Altcoins and Stablecoins"
slug: crypto-basics
shortTitle: "Crypto Basics"
description: "What a cryptocurrency is, how Bitcoin and Ethereum differ, why altcoins swing so hard and what stablecoins are for, explained for complete beginners."
level: beginner
category: crypto
course: beginner
order: 2
topics: [bitcoin, ethereum, altcoins, stablecoins]
publishedAt: 2026-10-19
updatedAt: 2026-10-19
featured: false
popular: false
cover: /academy/img/covers/crypto-basics.svg
faqs:
  - q: "Who controls Bitcoin?"
    a: "No single company or government. The rules are enforced by software run by thousands of independent computers around the world, and changing them requires broad agreement among the people who run that software."
  - q: "Is Ethereum the same as ether?"
    a: "Not quite. Ethereum is the network and ether (ETH) is the currency used on it. People often say Ethereum when they mean the coin, and on trading platforms it appears as ETH."
  - q: "What counts as an altcoin?"
    a: "Any cryptocurrency other than Bitcoin. Some people also leave Ethereum out of the term because of its size, but the idea is the same: smaller, usually more volatile coins."
  - q: "Can a stablecoin lose its value?"
    a: "Yes. Stablecoins aim to hold a fixed value, usually one US dollar, but they can drift below it during market stress, and some have failed completely. The quality of what backs a stablecoin matters a great deal."
  - q: "Do I need a crypto wallet to trade crypto on the platform?"
    a: "Not for the coins you trade. Crypto markets on the platform are CFDs, so you trade the price without holding the coin. You only need a wallet or exchange account if you choose to deposit or withdraw funds in crypto."
---

Crypto is one of the youngest asset classes in the world. Bitcoin launched in 2009, and since then thousands of other coins and tokens have appeared. Some have become large, widely used networks; many have disappeared. Crypto markets trade every hour of every day, and prices can move further in a weekend than some markets move in a year.

This lesson explains what a cryptocurrency actually is and introduces the four groups you will hear about most: Bitcoin, Ethereum, altcoins and stablecoins. It finishes with how trading crypto on {{BRAND}} differs from buying coins on an exchange.

<CTA label="See the crypto markets" to="/markets" />

<KeyTakeaways>

- **Crypto runs on blockchains.** A blockchain is a shared public record of transactions kept by many computers instead of a single bank.
- **Bitcoin and Ethereum do different jobs.** Bitcoin is a scarce digital asset with a fixed supply cap; Ethereum is a network for running programs, paid for in ether.
- **Altcoins carry extra risk.** Smaller coins tend to move further and faster than Bitcoin, in both directions, and many have faded away entirely.
- **Stablecoins are the cash of crypto.** Tokens like USDT aim to stay worth one US dollar, which is why {{BRAND}} uses USDT for balances and prices.

</KeyTakeaways>

## What a cryptocurrency is

A **cryptocurrency** is a digital token whose ownership is recorded on a **blockchain**: a public ledger that lists every transaction, grouped into blocks that are linked together in order. Thousands of computers keep copies of this ledger and follow the same rules to agree on which transactions are valid.

To move coins, you sign a transaction with a **private key**, a secret code that proves the coins are yours. A **wallet** is simply software or a device that stores those keys. Whoever holds the key controls the coins, and a transaction cannot be reversed once the network has confirmed it.

## Bitcoin: the first and the largest

Bitcoin was described in 2008 by a person or group using the name Satoshi Nakamoto, and the network went live in January 2009. It has remained the largest cryptocurrency by market value ever since.

Three features define it:

- **A fixed supply.** There will never be more than 21 million bitcoin. New coins are released to the computers that secure the network, and that reward halves roughly every four years, an event known as the **halving**.
- **Proof of work.** Computers called miners compete to add each new block, which uses a lot of electricity but makes the history very hard to rewrite.
- **Divisibility.** One bitcoin splits into 100 million units called satoshis, so nobody needs to buy a whole coin.

Because its supply schedule is known in advance, Bitcoin's price is driven mostly by changes in demand. Supporters often compare it to digital gold; critics point to its large price swings.

## Ethereum: a programmable blockchain

Ethereum launched in 2015 with a different aim. As well as moving coins, it can run **smart contracts**: programs stored on the blockchain that execute automatically when their conditions are met. Lending apps, exchanges without a middleman and many stablecoins are built this way.

The network's currency is **ether (ETH)**. Every action on Ethereum costs a small fee called **gas**, paid in ETH, so demand for the network feeds into demand for the coin. In 2022 Ethereum switched from proof of work to **proof of stake**, where validators lock up ETH as a deposit instead of using mining hardware. Unlike Bitcoin, ETH has no fixed supply cap.

## Altcoins: everything else

An **altcoin** is any cryptocurrency other than Bitcoin. The group is huge and mixed. It includes other smart-contract networks such as Solana, coins built for fast payments, tokens tied to a particular app or exchange, and **meme coins** that started as jokes and trade mainly on attention.

A few patterns hold for most of them:

- They are smaller, so it takes less money to move their price.
- They are often less liquid, so spreads can be wider.
- They frequently move in the same direction as Bitcoin, but further.
- Many projects from earlier cycles have lost most of their value or stopped trading.

## Stablecoins: crypto's version of cash

A **stablecoin** is a token designed to hold a steady value, usually one US dollar. The best known are backed by reserves of cash and short-term government debt held by the company that issues them. USDT, issued by Tether, is the largest example.

Traders use stablecoins to park money between trades, to move funds quickly between platforms, and as a common unit for pricing everything else. Not every design has held up: in 2022 an algorithmic stablecoin called TerraUSD, which relied on a trading mechanism rather than reserves, lost its peg and collapsed. [What Is USDT and Why We Trade Against It](/academy/article/what-is-usdt) covers stablecoins in more detail.

<CompareTable caption="The four groups at a glance">

| | Bitcoin | Ethereum | Altcoins | Stablecoins |
| --- | --- | --- | --- | --- |
| Main purpose | Scarce digital money | A network for apps and contracts | Varies by project | A steady dollar unit |
| Supply | Capped at 21 million | No fixed cap | Varies widely | Grows and shrinks with demand |
| Price behavior | Volatile | Volatile, often more than Bitcoin | Often the most volatile | Aims to stay at one dollar |
| On {{BRAND}} | BTC/USDT | ETH/USDT | For example SOL/USDT | USDT is your account currency |

</CompareTable>

## Why crypto prices move

Crypto prices respond to many of the same forces as other markets, plus a few of their own:

1. **Demand against supply.** With supply fixed or predictable, shifts in demand show up directly in the price.
2. **Global risk appetite.** When interest rates rise or investors turn cautious, speculative assets including crypto often come under pressure.
3. **Regulation and legal news.** Approvals, bans and court rulings can move prices sharply.
4. **Large flows.** Big buyers and sellers, including funds that hold crypto, can shift the balance quickly.
5. **Network and industry events.** Halvings, upgrades, hacks and the failure of a major exchange have all moved the market.
6. **Leverage elsewhere.** When many traders on other venues are using borrowed money, a sharp move can force them to close, which pushes the price further in the same direction.

[Market Sentiment](/academy/article/market-sentiment) explains how traders try to read that crowd behavior.

<Example title="Why volatility matters (illustrative prices)">

You hold two positions, each worth 1,000 USDT.

- BTC/USDT moves 4%: 1,000 × 4% = **40 USDT**. At a price of 60,000, that is a move of 60,000 × 4% = 2,400 USDT per coin.
- SOL/USDT moves 10% in the same period: 1,000 × 10% = **100 USDT**.

The positions are the same size, but the altcoin swings two and a half times as much.

</Example>

## Owning crypto versus trading it on {{BRAND}}

When you buy a coin on an exchange, you own it. You can send it to a wallet, but you are also responsible for keeping the keys safe, and you only profit if the price rises.

On {{BRAND}}, every crypto market is a CFD settled in USDT. You trade the price of BTC/USDT or ETH/USDT without holding the coin, which means:

- You can open a **Buy** if you expect a rise or a **Sell** if you expect a fall.
- There is no coin to store or send, so no keys to lose for that position.
- You can use leverage, which magnifies losses as well as gains.
- Positions held overnight may carry a daily swap. Crypto markets are charged every day, including weekends, at {{SWAP_HOUR}}.

{/* TODO: verify with platform — current crypto swap rates (they may be zero on some markets). */}

[What Is a CFD?](/academy/article/what-is-a-cfd) walks through the contract itself.

<Warning>

Crypto can move sharply at any hour, including nights and weekends when you may not be watching. Combined with leverage, a fast move can wipe out the margin behind a position in minutes. Always use a stop loss and keep positions small enough that a bad day stays a bad day.

</Warning>

<ProTip>

Because crypto never closes, price alerts are especially useful. Tap the bell on any market row, or go to Account → Alerts, to be notified by push or email when a coin reaches a level you care about.

</ProTip>

## Conclusion

Cryptocurrencies are digital tokens tracked on public blockchains. Bitcoin is the original, with a fixed supply of 21 million. Ethereum adds programmable smart contracts paid for in ether. Altcoins cover everything else and tend to move further and faster, while stablecoins aim to hold one dollar and act as the market's cash. Knowing which group a coin belongs to tells you a lot about how it is likely to behave. On {{BRAND}} you trade all of them as USDT-settled CFDs, so the next lesson explains USDT itself.
